import type { JanusCtx } from './context.js';
import { onMaterialize, restoreUnreadEntry, unreadEntry } from './frame-snapshot.js';

const journals = new WeakMap<object, { view: JanusCtx; wrap: <T>(value: T) => T; begin: () => { restore: () => void; release: () => void } }>();
const sources = new WeakMap<object, object>();

/** Unwrap one guarded row for native read-only consumers such as structuredClone. */
export function boundarySource<T>(value: T): T {
  let source = value;
  while (source && typeof source === 'object' && sources.has(source)) source = sources.get(source) as T;
  return source;
}

/** Bind a caller's retained row to the same journal without looking up or copying a whole holder. */
export function boundaryValue<T>(ctx: JanusCtx, value: T): T {
  return journals.get(ctx)!.wrap(value);
}

function probe(kind: 'begin' | 'write' | 'restore' | 'release'): void {
  (globalThis as typeof globalThis & { ftBoundaryJournalProbe?: (kind: string) => void }).ftBoundaryJournalProbe?.(kind);
}
const external = new Set(['read', 'roll', 'originals', 'catalog', 'randomTableSeen', 'randomPickProbe', 'strayProbe']);

/** Effect frames expose mutable state through one identity-preserving view. Checkpoints
 * retain a shallow before-image only on the first write to an object, including nested attempts.
 * Reading a lazy snapshot is not a write. No whole-encounter checkpoint or restore walk is needed. */
export function journalBoundaryContext(ctx: JanusCtx): JanusCtx {
  const held = journals.get(ctx); if (held) return held.view;
  const proxies = new WeakMap<object, object>();
  type Journal = { saved: Map<object, () => void>; materialized: Map<object, Map<string, unknown>> };
  const active = new Set<Journal>();
  const raw = boundarySource;
  // Spreads, filters and new containers may hold guarded children. Normalize only the
  // assigned graph in place; existing guarded state is already plain and stays unvisited.
  const store = <T>(value: T, seen?: WeakSet<object>): T => {
    const source = raw(value);
    if (!source || typeof source !== 'object' || source !== value || proxies.has(source)) return source;
    seen ??= new WeakSet<object>();
    if (seen.has(source)) return source;
    seen.add(source);
    if (source instanceof Map) {
      let changed = false;
      const entries = [...source].map(([key, entry]) => {
        const nextKey = store(key, seen), next = store(entry, seen);
        changed ||= nextKey !== key || next !== entry;
        return [nextKey, next] as const;
      });
      if (changed) { source.clear(); for (const [key, entry] of entries) source.set(key, entry); }
    } else if (source instanceof Set) {
      let changed = false;
      const entries = [...source].map(entry => {
        const next = store(entry, seen); changed ||= next !== entry; return next;
      });
      if (changed) { source.clear(); for (const entry of entries) source.add(entry); }
    } else for (const key of Reflect.ownKeys(source)) {
      const descriptor = Object.getOwnPropertyDescriptor(source, key)!;
      if (!('value' in descriptor)) continue;
      const next = store(descriptor.value, seen);
      if (next !== descriptor.value) Object.defineProperty(source, key, { ...descriptor, value: next });
    }
    return source;
  };
  const changing = (target: object): void => {
    for (const { saved, materialized } of active) {
      if (saved.has(target)) continue;
      let undo: () => void;
      if (target instanceof Map) {
        const entries = [...target];
        undo = () => { target.clear(); for (const [key, value] of entries) target.set(key, value); };
      } else if (target instanceof Set) {
        const entries = [...target];
        undo = () => { target.clear(); for (const value of entries) target.add(value); };
      } else {
        const descriptors = Object.getOwnPropertyDescriptors(target);
        const unread = Object.keys(descriptors).flatMap(key => {
          const entry = unreadEntry(target, key); return entry ? [[key, entry] as const] : [];
        });
        undo = () => {
          for (const key of Reflect.ownKeys(target)) if (!Object.prototype.hasOwnProperty.call(descriptors, key)) Reflect.deleteProperty(target, key);
          // Restore indices before length: a failed pop/splice/length truncation must keep holes and aliases.
          for (const key of Reflect.ownKeys(descriptors)) if (key !== 'length') Object.defineProperty(target, key, descriptors[key as string]!);
          if (descriptors.length) Object.defineProperty(target, 'length', descriptors.length);
          for (const [key, entry] of unread) {
            const got = materialized.get(target);
            if (got?.has(key)) Object.defineProperty(target, key, { value: got.get(key), writable: true, enumerable: true, configurable: true });
            else {
              Object.defineProperty(target, key, { value: undefined, writable: true, enumerable: true, configurable: true });
              restoreUnreadEntry(target, key, entry);
            }
          }
        };
      }
      probe('write'); saved.set(target, undo);
    }
  };
  const wrap = <T>(value: T): T => {
    if (!value || typeof value !== 'object') return value;
    const target = raw(value) as object;
    const held = proxies.get(target); if (held) return held as T;
    const proxy = new Proxy(target, {
      get(object, key) {
        // Native membership must accept both a caller-held row and its guarded alias.
        if (Array.isArray(object) && (key === 'includes' || key === 'indexOf' || key === 'lastIndexOf')) return (entry: unknown, ...from: number[]) => {
          const native = raw(entry), alias = native && typeof native === 'object' ? proxies.get(native) : undefined;
          const candidates = alias ? [native, entry, alias] : [native, entry];
          if (key === 'includes') return candidates.some(value => object.includes(value, from[0]));
          const indices = candidates.map(value => key === 'indexOf' ? object.indexOf(value, from[0]) : from.length ? object.lastIndexOf(value, from[0]) : object.lastIndexOf(value)).filter(index => index >= 0);
          return indices.length ? (key === 'indexOf' ? Math.min(...indices) : Math.max(...indices)) : -1;
        };
        if (object instanceof Map || object instanceof Set) {
          if (key === 'size') return object.size;
          const heldKey = (entry: unknown): unknown => {
            const native = raw(entry);
            if (object.has(native)) return native;
            const alias = native && typeof native === 'object' ? proxies.get(native) : undefined;
            return alias && object.has(alias) ? alias : native;
          };
          if (key === 'has') return (entry: unknown) => object.has(heldKey(entry));
          if (key === 'delete') return (entry: unknown) => { const held = heldKey(entry); if (!object.has(held)) return false; changing(object); return object.delete(held); };
          if (key === 'clear') return () => { if (object.size) { changing(object); object.clear(); } };
          if (object instanceof Map) {
            if (key === 'get') return (entry: unknown) => wrap(object.get(heldKey(entry)));
            if (key === 'set') return (entry: unknown, next: unknown) => {
              const held = heldKey(entry);
              if (!object.has(held) || !Object.is(object.get(held), raw(next))) { changing(object); object.set(store(held), store(next)); }
              return proxy;
            };
          } else if (key === 'add') return (entry: unknown) => {
            if (!object.has(heldKey(entry))) { changing(object); object.add(store(entry)); } return proxy;
          };
          if (key === 'forEach') return (callback: (value: unknown, key: unknown, host: object) => void, receiver?: unknown) =>
            object.forEach((entry, at) => callback.call(receiver, wrap(entry), wrap(at), proxy));
          if (key === Symbol.iterator || key === 'entries' || key === 'keys' || key === 'values') return function* () {
            const entries = key === 'entries' || key === Symbol.iterator && object instanceof Map;
            for (const entry of entries ? object.entries() : key === 'keys' ? object.keys() : object.values())
              yield entries ? (entry as unknown[]).map(wrap) : wrap(entry);
          };
        }
        const entry = Reflect.get(object, key, object);
        return typeof key === 'string' && external.has(key) ? entry : wrap(entry);
      },
      set(object, key, next) {
        const descriptor = Object.getOwnPropertyDescriptor(object, key);
        if (descriptor && 'value' in descriptor && Object.is(descriptor.value, raw(next))) return true;
        changing(object); return Reflect.set(object, key, store(next), object);
      },
      deleteProperty(object, key) { if (Object.prototype.hasOwnProperty.call(object, key)) changing(object); return Reflect.deleteProperty(object, key); },
      defineProperty(object, key, descriptor) { changing(object); return Reflect.defineProperty(object, key,
        'value' in descriptor ? { ...descriptor, value: store(descriptor.value) } : descriptor); },
    });
    proxies.set(target, proxy); sources.set(proxy, target);
    return proxy as T;
  };
  const result = wrap(ctx);
  const begin = () => {
    probe('begin');
    const saved = new Map<object, () => void>(), materialized = new Map<object, Map<string, unknown>>();
    const journal = { saved, materialized }; active.add(journal);
    const stop = onMaterialize((host, key, value) => {
      if (!saved.has(host)) return;
      let got = materialized.get(host); if (!got) materialized.set(host, got = new Map());
      got.set(key, value);
    });
    const release = (): void => { if (active.delete(journal)) probe('release'); stop(); };
    const restore = (): void => {
      release();
      if (saved.size) probe('restore');
      for (const undo of [...saved.values()].reverse()) undo();
      saved.clear();
    };
    return { restore, release };
  };
  const state = { view: result, wrap, begin };
  journals.set(ctx, state); journals.set(result, state);
  return result;
}

export function boundaryJournal(ctx: JanusCtx): { restore: () => void; release: () => void } | undefined {
  return journals.get(ctx)?.begin();
}
